import {
  pollAsyncTask,
  type AsyncTaskStartResponse,
} from "@/composables/useAsyncTask";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { goSolidApi } from "@/composables/useGoSolidApi";
import type { AxiosResponse } from "axios";
import { onScopeDispose, ref, shallowReactive, type Ref } from "vue";

interface IntegrationStatus {
  last_saved_at: string | null;
  saved_by: string;
  online?: boolean;
}

/** What a status-check task returns: each probed integration's name mapped to whether it answered. */
type RegistryStatusCheckResult = Record<string, boolean>;

/** Key of the check that probes every registry, beside the per-integration ones. */
const ALL_REGISTRIES = Symbol("all registries");

export function useRegistryStatus() {
  const { globalErrorHandler } = useGlobalHandlers();
  const integrationStatus = ref<Record<string, IntegrationStatus>>({});
  // Status checks in flight, so rows can be checked in parallel and a repeated click joins the
  // check already running. Unmounting aborts them, which stops their polling.
  const statusChecks = shallowReactive(
    new Map<string | typeof ALL_REGISTRIES, Promise<void>>()
  );
  const disposal = new AbortController();
  const isTestingAuth = ref<Record<string, boolean>>({});
  const isTestingCompanies = ref<Record<string, boolean>>({});
  const isTestingDevices = ref<Record<string, boolean>>({});
  const isTestingCertificates = ref<Record<string, boolean>>({});
  const isTestingIssuanceHistory = ref<Record<string, boolean>>({});
  const isTestingTransfer = ref<Record<string, boolean>>({});
  const isSyncingCompanies = ref<Record<string, boolean>>({});

  onScopeDispose(() => disposal.abort());

  const fetchRegistriesData = async () => {
    try {
      const response = await goSolidApi.get("/api/admin/registry/integrations");
      integrationStatus.value = response.data;
    } catch (error) {
      globalErrorHandler(error);
    }
  };

  /** Whether a registry's status is being checked: by its own check, or by a check of every registry. */
  const isCheckingStatus = (integrationName: string) =>
    statusChecks.has(ALL_REGISTRIES) || statusChecks.has(integrationName);

  /**
   * Probe one registry, or every registry when no name is given, and record whether each is online.
   *
   * The probe runs as a backend task; this resolves once it has settled, and never rejects — a
   * failure is reported through the global error handler. A call for a check that is already
   * running joins that run instead of starting another.
   */
  const checkRegistryStatus = (integrationName?: string): Promise<void> => {
    const key = integrationName ?? ALL_REGISTRIES;
    const running = statusChecks.get(key);
    if (running) return running;

    const check = runStatusCheck(integrationName).finally(() =>
      statusChecks.delete(key)
    );
    statusChecks.set(key, check);
    return check;
  };

  const runStatusCheck = async (integrationName?: string) => {
    try {
      const response = await goSolidApi.post<AsyncTaskStartResponse>(
        "/api/admin/registry/status-checks",
        integrationName ? { integration: integrationName } : {}
      );
      applyOnlineStatuses(
        await pollAsyncTask<RegistryStatusCheckResult>(response.data.task_id, {
          signal: disposal.signal,
        })
      );
    } catch (error) {
      if (!disposal.signal.aborted) globalErrorHandler(error);
    }
  };

  const applyOnlineStatuses = (statuses: RegistryStatusCheckResult) => {
    for (const [integrationName, online] of Object.entries(statuses)) {
      integrationStatus.value[integrationName] = {
        ...(integrationStatus.value[integrationName] ?? {
          last_saved_at: null,
          saved_by: "",
        }),
        online,
      };
    }
  };

  const runFlagged = async (
    flags: Ref<Record<string, boolean>>,
    integrationName: string,
    request: () => Promise<AxiosResponse>
  ) => {
    flags.value[integrationName] = true;
    try {
      const response = await request();
      return response.data;
    } catch (error) {
      globalErrorHandler(error);
      throw error;
    } finally {
      flags.value[integrationName] = false;
    }
  };

  const testRegistryAuth = (integrationName: string) =>
    runFlagged(isTestingAuth, integrationName, () =>
      goSolidApi.post("/api/admin/registry/run-auth", {
        integration: integrationName,
      })
    );

  const testRegistryCompanies = (integrationName: string) =>
    runFlagged(isTestingCompanies, integrationName, () =>
      goSolidApi.post("/api/admin/registry/test-companies", {
        integration: integrationName,
      })
    );

  const testRegistryDevices = (
    integrationName: string,
    registryCompanyId: string
  ) =>
    runFlagged(isTestingDevices, integrationName, () =>
      goSolidApi.post("/api/admin/registry/test-devices", {
        integration: integrationName,
        registry_company_id: registryCompanyId,
      })
    );

  const testRegistryCertificates = (
    integrationName: string,
    registryCompanyId: string
  ) =>
    runFlagged(isTestingCertificates, integrationName, () =>
      goSolidApi.post("/api/admin/registry/test-certificates", {
        integration: integrationName,
        registry_company_id: registryCompanyId,
      })
    );

  const testRegistryIssuanceHistory = (
    integrationName: string,
    registryCompanyId: string
  ) =>
    runFlagged(isTestingIssuanceHistory, integrationName, () =>
      goSolidApi.post("/api/admin/registry/test-issuance-history", {
        integration: integrationName,
        registry_company_id: registryCompanyId,
      })
    );

  const testRegistryTransfer = (
    integrationName: string,
    certificateId: string,
    fromAccount: string,
    toAccount: string
  ) =>
    runFlagged(isTestingTransfer, integrationName, () =>
      goSolidApi.post("/api/admin/registry/test-transfer", {
        integration: integrationName,
        certificate_id: certificateId,
        from_account: fromAccount,
        to_account: toAccount,
      })
    );

  const syncCompaniesFromRegistry = (integrationName: string) =>
    runFlagged(isSyncingCompanies, integrationName, () =>
      goSolidApi.post(
        `/api/admin/registry/integrations/${integrationName}/sync-companies`
      )
    );

  return {
    integrationStatus,
    isCheckingStatus,
    isTestingAuth,
    isTestingCompanies,
    isTestingDevices,
    isTestingCertificates,
    isTestingIssuanceHistory,
    isTestingTransfer,
    isSyncingCompanies,
    fetchRegistriesData,
    checkRegistryStatus,
    testRegistryAuth,
    testRegistryCompanies,
    testRegistryDevices,
    testRegistryCertificates,
    testRegistryIssuanceHistory,
    testRegistryTransfer,
    syncCompaniesFromRegistry,
  };
}
